import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useChatStore } from '../../store/chatStore';
import { initials, avatarColor, channelDisplay } from '../chatHelpers';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import { createPortal } from 'react-dom';

/**
 * "Forward to…" popover: pick another conversation the current one's message
 * is copied into. Same controlled open/anchorRef/onClose recipe as
 * EmojiReactionPicker, with a searchable list instead of an emoji grid.
 */
export function ForwardChannelPicker({ open, anchorRef, excludeChannelId, onClose, onSelect }) {
  const channels = useChatStore((s) => s.channels);
  const currentUserId = useChatStore((s) => s.currentUserId);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  const bounds = useCallback(
    () => anchorRef.current?.closest('.chat-view')?.getBoundingClientRect() ?? null,
    [anchorRef]
  );
  const pos = usePopoverPosition({ anchorRef, open, width: 240, align: 'end', bounds });

  useEffect(() => {
    if (!open) {
      setQuery('');
      return undefined;
    }
    searchRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined' || !document.body) return null;

  const options = channels
    .filter((c) => c.id !== excludeChannelId)
    .map((c) => ({ id: c.id, ...channelDisplay(c, currentUserId) }))
    .filter((c) => !query.trim() || c.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return createPortal(
    <>
      <div
        className="emoji-reaction-backdrop"
        aria-hidden
        onMouseDown={(e) => {
          e.preventDefault();
          onClose?.();
        }}
      />
      <div
        className="chat-forward-popover"
        style={{
          position: 'fixed',
          ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          zIndex: 10040,
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="chat-forward-popover-search">
          <Search size={12} className="chat-forward-popover-search-icon" aria-hidden />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Forward to…"
            className="chat-forward-popover-search-input"
            aria-label="Search conversations"
          />
        </div>
        <div className="chat-forward-popover-list">
          {options.length === 0 && <div className="chat-forward-popover-empty">No conversations</div>}
          {options.map((c) => (
            <button
              key={c.id}
              type="button"
              className="chat-forward-popover-row"
              onClick={() => onSelect?.(c.id)}
            >
              <span
                className="chat-forward-popover-avatar"
                style={{ background: avatarColor(c.seed) }}
              >
                {initials(c.name.replace(/^#\s*/, ''))}
              </span>
              <span className="chat-forward-popover-name">{c.name}</span>
            </button>
          ))}
        </div>
      </div>
    </>,
    document.body
  );
}
